import { Observable } from 'rxjs/Observable'

import { ModelFactory } from '../'
import { QuestionTypeService } from '../../services'
import {
  AnswerChoice,
  AnswerChoiceGroup,
  QuestionPrompt,
  Question,
  Explanation,
  QUESTION_TYPES_ID_MAPPING,
  Label,
  LABEL_KEYS
} from '../../models'

import { AlphabetGenerator } from '../../../helpers'

interface Generator {
  next: () => string
}

interface SimpleQuestionOptions {
  answerChoiceGroups: Array<AnswerChoiceGroup>
  questionPrompts: Array<QuestionPrompt>
  explanations: Array<Explanation>
}

export class QuestionFactory {

  private _questionTypeToFactoryMethodMapping: Map<string, string> = new Map<string, string>()

  constructor(
    private _modelFactory: ModelFactory,
    private _alphabetGenerator: AlphabetGenerator,
    private _questionTypeService: QuestionTypeService
  ) {
    this._questionTypeToFactoryMethodMapping
      .set(QUESTION_TYPES_ID_MAPPING.QUANTITATIVE_COMPARISON, 'getQuantitativeComparison')
    this._questionTypeToFactoryMethodMapping.set(QUESTION_TYPES_ID_MAPPING.MULTIPLE_CHOICE, 'getMultipleChoice')
    this._questionTypeToFactoryMethodMapping.set(QUESTION_TYPES_ID_MAPPING.NUMERIC_ENTRY, 'createNumericEntry')
    this._questionTypeToFactoryMethodMapping.set(QUESTION_TYPES_ID_MAPPING.MULTI_SELECT, 'getMultiSelect')
    this._questionTypeToFactoryMethodMapping.set(QUESTION_TYPES_ID_MAPPING.TEXT_COMPLETION, 'createTextCompletion')
    this._questionTypeToFactoryMethodMapping.set(QUESTION_TYPES_ID_MAPPING.FLASH_CARD, 'createsFlashCard')
    this._questionTypeToFactoryMethodMapping.set(QUESTION_TYPES_ID_MAPPING.ESSAY, 'createEssay'),
    this._questionTypeToFactoryMethodMapping.set(QUESTION_TYPES_ID_MAPPING.SENTENCE_SELECT, 'createSentenceSelect')
    this._questionTypeToFactoryMethodMapping.set(QUESTION_TYPES_ID_MAPPING.TWO_PART_ANALYSIS,'createTwoPartAnalysis')
    this._questionTypeToFactoryMethodMapping.set(QUESTION_TYPES_ID_MAPPING.GRAPHICS_INTERPRETATION, 'createGraphicsInterpretation')
    this._questionTypeToFactoryMethodMapping.set(QUESTION_TYPES_ID_MAPPING.MULTI_SOURCE_REASONING, 'createMultiSource')
    this._questionTypeToFactoryMethodMapping.set(QUESTION_TYPES_ID_MAPPING.TABLE_ANALYSIS, 'createTableAnalysis')
    this._questionTypeToFactoryMethodMapping.set(QUESTION_TYPES_ID_MAPPING.DATA_SIFFICIENCY, 'getMultipleChoice')
    this._questionTypeToFactoryMethodMapping.set(QUESTION_TYPES_ID_MAPPING.PROBLEM_SOLVING, 'getMultipleChoice')
    this._questionTypeToFactoryMethodMapping.set(QUESTION_TYPES_ID_MAPPING.CRITICAL_REASONING, 'getMultipleChoice')
    this._questionTypeToFactoryMethodMapping.set(QUESTION_TYPES_ID_MAPPING.READING_COMPREHENSION, 'getMultipleChoice')
  }

  public getQuantitativeComparison(): Observable<Question> {
    const numberOfChoices = 4
    const quantityA = this._modelFactory.createAndInitialize<QuestionPrompt>(QuestionPrompt.name, {key: 'quantity_a'})
    const quantityB = this._modelFactory.createAndInitialize<QuestionPrompt>(QuestionPrompt.name, {key: 'quantity_b'})
    const options = this._createSimpleQuestionOptions(numberOfChoices)

    this._setQuantitativeComparisonChoices(options.answerChoiceGroups[0].answerChoices)

    const quantitativeComparisonOptions = {
      answerChoiceGroups: options.answerChoiceGroups,
      questionPrompts: options.questionPrompts.concat([quantityA, quantityB]),
      explanations: options.explanations
    }

    const question = this._modelFactory.createAndInitialize<Question>(
      Question.name,
      quantitativeComparisonOptions
    )

    return this._addQuestionType(question, QUESTION_TYPES_ID_MAPPING.QUANTITATIVE_COMPARISON)
  }

  public getMultipleChoice(): Observable<Question> {
    const numberOfChoices = 5
    const options = this._createSimpleQuestionOptions(numberOfChoices)
    const question = this._modelFactory.createAndInitialize<Question>(
      Question.name,
      options
    )

    return this._addQuestionType(question, QUESTION_TYPES_ID_MAPPING.MULTIPLE_CHOICE)
  }

  public createNumericEntry(): Observable<Question> {
    const numberOfChoices = 2
    const options = this._createSimpleQuestionOptions(numberOfChoices, this._nullAnswerChoiceLabelGenerator())
    const beforeLabel = this._createLabel(LABEL_KEYS.NUMERIC_ENTRY_BEFORE)
    const afterLabel = this._createLabel(LABEL_KEYS.NUMERIC_ENTRY_AFTER)
    options.answerChoiceGroups[0].labels = [beforeLabel, afterLabel]
    const question = this._modelFactory.createAndInitialize<Question>(
      Question.name,
      options
    )

    return this._addQuestionType(question, QUESTION_TYPES_ID_MAPPING.NUMERIC_ENTRY)
  }

  public createGraphicsInterpretation(): Observable<Question> {
    const numberOfChoices = 3
      const options = this._createSimpleQuestionOptions(numberOfChoices)
      const labelOptions = { key: "text" }
      options.answerChoiceGroups[0].labels = [this._modelFactory.createAndInitialize<Label>(
        Label.name,
        labelOptions
      )]
      options.answerChoiceGroups.push(this._modelFactory.createAndInitialize<AnswerChoiceGroup>(
        AnswerChoiceGroup.name,
        {
          answerChoices: this._createAnswerChoices(numberOfChoices, this._alphabetGenerator),
          sort: 2,
          labels: [this._modelFactory.createAndInitialize<Label>(
            Label.name,
            labelOptions
          )]
        }
      ))
      const question = this._modelFactory.createAndInitialize<Question>(
        Question.name,
        options
      )


      return this._addQuestionType(question, QUESTION_TYPES_ID_MAPPING.GRAPHICS_INTERPRETATION)
  }

  public createMultiSource(): Observable<Question> {
    const numberOfChoices = 3
    const labelGenarator = () => [this._createLabel(LABEL_KEYS.ANSWER_CHOICE_GROUP)]
    const options = this._createSimpleQuestionOptions(numberOfChoices, this._alphabetGenerator, 1, labelGenarator)
    const question = this._modelFactory.createAndInitialize<Question>(
      Question.name,
      options
    )

    return this._addQuestionType(question, QUESTION_TYPES_ID_MAPPING.MULTI_SOURCE_REASONING)
  }

  public createTextCompletion(): Observable<Question> {
      const numberOfChoices = 3
      const options = this._createSimpleQuestionOptions(numberOfChoices)
      const question = this._modelFactory.createAndInitialize<Question>(
        Question.name,
        options
      )

      return this._addQuestionType(question, QUESTION_TYPES_ID_MAPPING.TEXT_COMPLETION)
  }

  public createTwoPartAnalysis(): Observable<Question> {
    const numberOfChoices = 3
      const options = this._createSimpleQuestionOptions(numberOfChoices)
      const labelOptions = { key: "text" }
      options.answerChoiceGroups[0].labels = [this._modelFactory.createAndInitialize<Label>(
        Label.name,
        labelOptions
      )]
      options.answerChoiceGroups.push(this._modelFactory.createAndInitialize<AnswerChoiceGroup>(
        AnswerChoiceGroup.name,
        {
          answerChoices: this._createAnswerChoices(numberOfChoices, this._alphabetGenerator),
          sort: 2,
          labels: [this._modelFactory.createAndInitialize<Label>(
            Label.name,
            labelOptions
          )]
        }
      ))
      const question = this._modelFactory.createAndInitialize<Question>(
        Question.name,
        options
      )


      return this._addQuestionType(question, QUESTION_TYPES_ID_MAPPING.TWO_PART_ANALYSIS)
  }

  public createFlashCard(): Observable<Question> {
      const numberOfChoices = 1
      const options = this._createSimpleQuestionOptions(numberOfChoices)
      const question = this._modelFactory.createAndInitialize<Question>(
        Question.name,
        options
      )

      return this._addQuestionType(question, QUESTION_TYPES_ID_MAPPING.FLASH_CARD)
  }

  public createEssay(): Observable<Question> {
      const questionText = this._modelFactory.createAndInitialize(QuestionPrompt.name, {key: 'question_text'})
      const options = { questionPrompts: [questionText] }
      const question = this._modelFactory.createAndInitialize<Question>(
        Question.name,
        options
      )

      return this._addQuestionType(question, QUESTION_TYPES_ID_MAPPING.ESSAY)
  }

  public createSentenceSelect(): Observable<Question> {
    const numberOfChoices = 1
    const options = this._createSimpleQuestionOptions(numberOfChoices)
    const question = this._modelFactory.createAndInitialize<Question>(
      Question.name,
      options
    )

    return this._addQuestionType(question, QUESTION_TYPES_ID_MAPPING.SENTENCE_SELECT)
  }

  public getMultiSelect(): Observable<Question> {
    const numberOfChoices = 5
    const options = this._createSimpleQuestionOptions(numberOfChoices)
    const question = this._modelFactory.createAndInitialize<Question>(
      Question.name,
      options
    )

    return this._addQuestionType(question, QUESTION_TYPES_ID_MAPPING.MULTI_SELECT)
  }

  public getByQuestionTypeId(questionTypeId: string): Observable<Question> {
    const factoryMethodName = this._questionTypeToFactoryMethodMapping.get(questionTypeId)
    if (! factoryMethodName) {
      throw new Error(`No factory method registered for question type id ${questionTypeId}`)
    }
    return this[factoryMethodName]()
  }

  public createTableAnalysis(): Observable<Question> {
    const numberOfChoices = 2
    const labelGenarator = () => [this._createLabel(LABEL_KEYS.ANSWER_CHOICE_GROUP)]
    const options = this._createSimpleQuestionOptions(numberOfChoices, this._alphabetGenerator, 3, labelGenarator)
    const question = this._modelFactory.createAndInitialize<Question>(
      Question.name,
      options
    )

    return this._addQuestionType(question, QUESTION_TYPES_ID_MAPPING.TABLE_ANALYSIS)
  }

  private _createSimpleQuestionOptions(
    numberOfChoices: number,
    choiceLabelIterator: Generator = this._alphabetGenerator,
    numberOfGroups = 1,
    labelsGenerator = () => []
  ): SimpleQuestionOptions {
    const explanation = this._modelFactory.instantiateClass<Explanation>(Explanation.name)
    const questionText = this._modelFactory.createAndInitialize<QuestionPrompt>(QuestionPrompt.name, {key: 'question_text'})
    const answerChoicesGenerator = () => this._createAnswerChoices(numberOfChoices, choiceLabelIterator)
    const answerChoiceGroups = this._createAnswerChoiceGroups(numberOfGroups, answerChoicesGenerator, labelsGenerator)
    return {
      explanations: [explanation],
      questionPrompts: [questionText],
      answerChoiceGroups: answerChoiceGroups
    }
  }

  private _createAnswerChoices(numberOfChoices: number, labelGenarator: Generator): AnswerChoice[] {
    this._alphabetGenerator.reset()
    return Array(numberOfChoices).fill(null)
      .map((_ignore, index) => this._modelFactory.createAndInitialize<AnswerChoice>(
        AnswerChoice.name,
        { value: '', label: labelGenarator.next(), sort: index }
      ))
  }

  private _setQuantitativeComparisonChoices(answerChoices: AnswerChoice[]) {
    answerChoices[0].value = 'Quantity A is greater'
    answerChoices[1].value = 'Quantity B is greater'
    answerChoices[2].value = 'Both Quantities are equal'
    answerChoices[3].value = 'Not enough information'
  }

  private _addQuestionType(question: Question, questionTypeID: string): Observable<Question> {
    return this._questionTypeService.findModelById(questionTypeID, ['displayType'])
      .map((questionType) => {
        question.questionType = questionType
        return question
      })
  }

  private _createLabel(key: string): Label {
    return this._modelFactory.createAndInitialize<Label>(Label.name, {
      key: key,
      value: '',
    })
  }

  private _createAnswerChoiceGroups(
    numberOfGroups = 1,
    answerChoicesGenerator: () => Array<AnswerChoice> = () => [],
    labelsGenerator: () => Array<Label> = () => []
  ): Array<AnswerChoiceGroup> {
    return Array(numberOfGroups).fill(null)
      .map((_ignore, index) => {
        return this._modelFactory.createAndInitialize<AnswerChoiceGroup>(
          AnswerChoiceGroup.name,
          {
            answerChoices: answerChoicesGenerator(),
            sort: index+1,
            labels: labelsGenerator()
          }
        )
      })
  }

  private _nullAnswerChoiceLabelGenerator(): Generator {
    return {
      next: () => ''
    }
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/data/factories/question-factory/question.factory.ts